import './loanDocumentListPage.scss';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import useLoanDocumentStore from '../../../store/loanDocumentStore/useLoanDocumentStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { handleSubmit } = useForm();

  const {
    computed: {
      hasNotProvidedDocuments,
      hasProvidedGAPDocuments,
      hasProvidedRICDocuments,
      hasProvidedVSCDocuments,
      numberOfGAPDocuments,
      numberOfRICDocuments,
      numberOfVSCDocuments,
      percent,
    },
  } = useLoanDocumentStore();

  const previousStep = (): void => {
    navigate('/loan-documents');
  };

  const formSubmit = (): void => {
    navigate('/loan-documents/current-mileage');
  };

  return (
    <div className="loan-document-list-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={percent}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main>
            <form
              data-testid={locator.pages.loanDocumentList.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentList.form.h1}>
                  {t('LoanDocumentListPage.title')}
                </h1>
                <h2 data-testid={locator.pages.loanDocumentList.form.h2}>
                  {t('LoanDocumentListPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <ul>
                    <li
                      data-testid={
                        locator.pages.loanDocumentList.form.vscListItemRoot
                      }
                    >
                      <Link
                        className={hasProvidedVSCDocuments ? 'provided' : ''}
                        data-testid={
                          locator.pages.loanDocumentList.form.vscListItemLink
                        }
                        to="/loan-documents/vsc-upload"
                      >
                        <div>
                          {hasProvidedVSCDocuments ? (
                            <CheckBoxIcon className="provided" />
                          ) : (
                            <DriveFolderUploadOutlinedIcon />
                          )}
                          <div className="title">
                            <h3>{t('LoanDocumentListPage.vsc')}</h3>
                            <span>
                              {hasProvidedVSCDocuments
                                ? t('LoanDocumentListPage.edit')
                                : t('LoanDocumentListPage.upload')}
                            </span>
                          </div>
                          <span className="subtitle">
                            {hasProvidedVSCDocuments
                              ? t('LoanDocumentListPage.numberOfDocs', {
                                  count: numberOfVSCDocuments,
                                })
                              : t('LoanDocumentListPage.tapToUpload')}
                          </span>
                        </div>
                      </Link>
                    </li>

                    <li
                      data-testid={
                        locator.pages.loanDocumentList.form.gapListItemRoot
                      }
                    >
                      <Link
                        className={hasProvidedGAPDocuments ? 'provided' : ''}
                        data-testid={
                          locator.pages.loanDocumentList.form.gapListItemLink
                        }
                        to="/loan-documents/gap-upload"
                      >
                        <div>
                          {hasProvidedGAPDocuments ? (
                            <CheckBoxIcon className="provided" />
                          ) : (
                            <DriveFolderUploadOutlinedIcon />
                          )}
                          <div className="title">
                            <h3>{t('LoanDocumentListPage.gap')}</h3>
                            <span>
                              {hasProvidedGAPDocuments
                                ? t('LoanDocumentListPage.edit')
                                : t('LoanDocumentListPage.upload')}
                            </span>
                          </div>
                          <span className="subtitle">
                            {hasProvidedGAPDocuments
                              ? t('LoanDocumentListPage.numberOfDocs', {
                                  count: numberOfGAPDocuments,
                                })
                              : t('LoanDocumentListPage.tapToUpload')}
                          </span>
                        </div>
                      </Link>
                    </li>

                    <li
                      data-testid={
                        locator.pages.loanDocumentList.form.ricListItemRoot
                      }
                    >
                      <Link
                        className={hasProvidedRICDocuments ? 'provided' : ''}
                        data-testid={
                          locator.pages.loanDocumentList.form.ricListItemLink
                        }
                        to="/loan-documents/ric-upload"
                      >
                        <div>
                          {hasProvidedRICDocuments ? (
                            <CheckBoxIcon className="provided" />
                          ) : (
                            <DriveFolderUploadOutlinedIcon />
                          )}
                          <div className="title">
                            <h3>{t('LoanDocumentListPage.ric')}</h3>
                            <span>
                              {hasProvidedRICDocuments
                                ? t('LoanDocumentListPage.edit')
                                : t('LoanDocumentListPage.upload')}
                            </span>
                          </div>
                          <span className="subtitle">
                            {hasProvidedRICDocuments
                              ? t('LoanDocumentListPage.numberOfDocs', {
                                  count: numberOfRICDocuments,
                                })
                              : t('LoanDocumentListPage.tapToUpload')}
                          </span>
                        </div>
                      </Link>
                    </li>
                  </ul>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.loanDocumentList.header.submitButton
                    }
                    disabled={hasNotProvidedDocuments}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('LoanDocumentListPage.continue')}
                  </Button>
                </div>
              </div>
              <footer data-testid={locator.pages.loanDocumentList.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentListPage';
